import { buildTtsRootAttributes, type ResolvedNoticeAppearance } from "../shared/appearance";
import { NOTICE_ID_ATTRIBUTE, STYLE_RADII } from "../appearance/constants";
import { resolveNoticeLook } from "../appearance/host-settings";
import { part } from "../appearance/parts";
import { scaleFontSizes, textScaleFactor } from "../appearance/text-scale";
import { buildThemeCssVariables } from "../appearance/theme-vars";
import type { Style } from "../appearance/types";
import { buildVariantStyles, mergeNoticeStyles } from "../appearance/variant-styles";
import { NOTICE_FONT_ROOT_ATTRIBUTE } from "../constants";
import { styles as classicStyles, type NoticeStyles } from "../styles/modal";
import { resolveLogoPosition } from "../modal/layout";
import type { LogoPosition, Settings } from "../api/types";
import { ASSISTED_DEFAULT_PRIMARY_COLOR } from "./constants";
import { hexToRgba } from "./theme";
import type { AssistedActiveConfig } from "./types";

const DEFAULT_CHEVRON_COLOR = "#323B4B";

export type AssistedLookInput = Readonly<{
  noticeId: string;
  content: AssistedActiveConfig;
  settings?: Partial<Settings>;
  prefersDark: boolean;
  isMobile: boolean;
}>;

/**
 * The console look, display only: the palette, the minimal or soft shape,
 * corners and text size. Glass keeps classic's surfaces here, since the
 * assisted notice draws no sheet. Nothing here changes what the visitor can
 * do, what is submitted or how it is verified.
 */
export type AssistedLook = Readonly<{
  appearance: ResolvedNoticeAppearance;
  styles: NoticeStyles;
  responsive: Record<string, Style>;
  primaryColor: string;
  linkColor: string;
  accentTint: string;
  accentSoft: string;
  chevronColor: string;
  logoPosition: LogoPosition;
  /** Accept All, Accept Selected (outlined) and Decline, each only where set. */
  acceptAll: Style;
  acceptSelected: Style;
  decline: Style;
  rootAttributes: Record<string, string>;
  rootVariables: Record<string, string>;
}>;

const responsiveFor = (isMobile: boolean): Record<string, Style> => ({
  title: { fontSize: isMobile ? "16px" : "18px" },
  subTitle: { fontSize: isMobile ? "14px" : "16px" },
  optionTitle: { fontSize: isMobile ? "14px" : "16px" },
  optionDescription: { fontSize: isMobile ? "12px" : "14px" },
  dataElementText: { fontSize: isMobile ? "12px" : "14px" },
  privacyText: { fontSize: isMobile ? "14px" : "16px" },
  dpoText: { fontSize: isMobile ? "13px" : "14px" },
  topRight: {
    fontSize: isMobile ? "11px" : "12px",
    padding: isMobile ? "3px 6px" : "3px 9px",
  },
  logo: {
    maxWidth: isMobile ? "96px" : "140px",
    maxHeight: isMobile ? "28px" : "32px",
  },
  languageItem: {
    fontSize: isMobile ? "11px" : "12px",
    padding: isMobile ? "6px 10px" : "8px 12px",
  },
  button: {
    fontSize: isMobile ? "14px" : "16px",
    padding: isMobile ? "10px 20px" : "9px 45px",
  },
});

export const buildAssistedLook = ({
  noticeId,
  content,
  settings,
  prefersDark,
  isMobile,
}: AssistedLookInput): AssistedLook => {
  const appearance = resolveNoticeLook(content.appearance, settings, prefersDark);
  const { colors } = appearance;
  const primaryColor =
    colors.accept_all_bg || content.primary_color || ASSISTED_DEFAULT_PRIMARY_COLOR;
  const radii = STYLE_RADII[appearance.style];
  const factor = textScaleFactor(appearance);
  const styles = scaleFontSizes(
    buildVariantStyles(
      mergeNoticeStyles(classicStyles as NoticeStyles, {
        modal: { borderRadius: appearance.radius || radii.modal },
        button: { borderRadius: appearance.radius || radii.button },
      }),
      appearance.style,
      colors,
      primaryColor
    ),
    factor
  );

  return {
    appearance,
    styles,
    responsive: scaleFontSizes(responsiveFor(isMobile), factor),
    primaryColor,
    linkColor: colors.link || content.secondary_color || ASSISTED_DEFAULT_PRIMARY_COLOR,
    accentTint: hexToRgba(primaryColor, 0.08) || "#F5F8FF",
    accentSoft: hexToRgba(primaryColor, 0.18) || "rgba(79, 135, 255, 0.18)",
    chevronColor: colors.heading ?? DEFAULT_CHEVRON_COLOR,
    logoPosition: resolveLogoPosition(content.logo_position),
    acceptAll: {
      backgroundColor: primaryColor,
      ...(colors.accept_all_text ? { color: colors.accept_all_text } : {}),
    },
    acceptSelected: {
      ...(colors.accept_bg ? { backgroundColor: colors.accept_bg } : {}),
      color: colors.accept_text ?? primaryColor,
      borderColor: colors.accept_text ?? primaryColor,
    },
    decline: {
      ...(colors.decline_bg ? { backgroundColor: colors.decline_bg } : {}),
      ...(colors.decline_text ? { color: colors.decline_text } : {}),
      ...(colors.decline_border ? { borderColor: colors.decline_border } : {}),
    },
    // The admin's custom CSS is scoped to the element carrying the notice id.
    rootAttributes: {
      [NOTICE_ID_ATTRIBUTE]: noticeId,
      [NOTICE_FONT_ROOT_ATTRIBUTE]: "",
      ...part("root"),
      ...buildTtsRootAttributes(colors),
    },
    rootVariables: {
      "--ra-accent": primaryColor,
      "--ra-accent-soft": hexToRgba(primaryColor, 0.18) || "rgba(79, 135, 255, 0.18)",
      ...buildThemeCssVariables(colors),
    },
  };
};
